/**
 * People → User groups (Slack; logic/userGroups, contracts userGroups.ts): the list (active / disabled tabs, search),
 * create and edit (name, handle — suggested from the name until typed —, description, members, default channels),
 * disable / re-enable, the workspace policy ("who can manage user groups") and the group card (clicking a group
 * mention or a row) with "Join default channels". Groups come from the workspace cache (bootstrap + user_group.updated);
 * my own saves show at once (the API's answer overlays the cache until the event catches up). Online only.
 */
import type { UserGroup, UserGroupPolicy } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { ApiFailure } from "../ports.ts";
import {
  DEFAULT_GROUP_POLICY,
  type GroupCard,
  type GroupFilter,
  type GroupRow,
  canManageUserGroups,
  canSetGroupPolicy,
  groupCard,
  groupFormProblem,
  groupFormProblemMessage,
  groupRows,
  normalizeGroupHandle,
  suggestGroupHandle,
} from "../logic/userGroups.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type UserGroupEditor = {
  /** Absent = creating. */
  id?: string;
  name: string;
  /** Without the "@". */
  handle: string;
  /** The handle was typed (stop suggesting it from the name). */
  handleEdited: boolean;
  description: string;
  memberIds: string[];
  channelIds: string[];
  problem?: MessageDescriptor;
  saving: boolean;
};

export type UserGroupsState = {
  filter: GroupFilter;
  query: string;
  rows: GroupRow[];
  counts: Record<GroupFilter, number>;
  /** I may create, edit and disable groups (policy and role). */
  canManage: boolean;
  policy: UserGroupPolicy;
  /** I may change the policy (owners and admins). */
  canSetPolicy: boolean;
  /** The create / edit sheet; absent = closed. */
  editor?: UserGroupEditor;
  /** The open group card. */
  card?: GroupCard;
  /** A failed disable / enable / join / policy change. */
  error?: MessageDescriptor;
};

export class UserGroupsViewModel extends ViewModel<UserGroupsState> {
  /** My saves, until the realtime event brings the same (or a newer) version. */
  private local: Record<string, UserGroup> = {};

  constructor(private readonly ws: WorkspaceViewModel) {
    super({ filter: "active", query: "", rows: [], counts: { active: 0, disabled: 0 }, canManage: false, policy: DEFAULT_GROUP_POLICY, canSetPolicy: false });
    this.onDispose(ws.subscribe(() => this.derive()));
    this.derive();
  }

  /** Every group: the cache with my newer saves on top. */
  groups(): UserGroup[] {
    const out: Record<string, UserGroup> = { ...this.ws.state.cache?.userGroups };
    for (const id of Object.keys(this.local)) {
      const mine = this.local[id]!;
      const cached = out[id];
      if (cached === undefined || cached.updatedAt < mine.updatedAt) out[id] = mine;
      else delete this.local[id];
    }
    return Object.values(out);
  }

  group(id: string): UserGroup | undefined {
    return this.groups().find((g) => g.id === id);
  }

  setFilter(filter: GroupFilter): void {
    this.update({ filter });
    this.derive();
  }

  setQuery(query: string): void {
    this.update({ query });
    this.derive();
  }

  openCreate(): void {
    if (!this.state.canManage) return;
    this.update({ editor: { name: "", handle: "", handleEdited: false, description: "", memberIds: [], channelIds: [], saving: false } });
  }

  openEdit(id: string): void {
    const g = this.group(id);
    if (g === undefined || !this.state.canManage) return;
    this.update({
      editor: { id, name: g.name, handle: g.handle, handleEdited: true, description: g.description ?? "", memberIds: [...g.memberIds], channelIds: [...g.channelIds], saving: false },
    });
  }

  setName(name: string): void {
    this.edit((e) => ({ ...e, name, handle: e.handleEdited ? e.handle : suggestGroupHandle(name) }));
  }

  setHandle(handle: string): void {
    this.edit((e) => ({ ...e, handle: normalizeGroupHandle(handle), handleEdited: true }));
  }

  setDescription(description: string): void {
    this.edit((e) => ({ ...e, description }));
  }

  toggleMember(userId: string): void {
    this.edit((e) => ({ ...e, memberIds: e.memberIds.includes(userId) ? e.memberIds.filter((x) => x !== userId) : [...e.memberIds, userId] }));
  }

  toggleChannel(channelId: string): void {
    this.edit((e) => ({ ...e, channelIds: e.channelIds.includes(channelId) ? e.channelIds.filter((x) => x !== channelId) : [...e.channelIds, channelId] }));
  }

  closeEditor(): void {
    if (this.state.editor === undefined) return;
    const { editor: _e, ...rest } = this.state;
    this.setState(rest);
  }

  async save(): Promise<void> {
    const e = this.state.editor;
    if (e === undefined || e.saving) return;
    const cache = this.ws.state.cache;
    const handles = Object.values(cache?.members ?? {}).flatMap((m) => (m.handle === undefined ? [] : [m.handle]));
    const problem = groupFormProblem({ name: e.name, handle: e.handle, description: e.description }, this.groups(), handles, e.id);
    if (problem !== undefined) {
      this.update({ editor: { ...e, problem: groupFormProblemMessage(problem) } });
      return;
    }
    const { problem: _p, ...clean } = e;
    this.update({ editor: { ...clean, saving: true } });
    const body = { name: e.name.trim(), handle: normalizeGroupHandle(e.handle), description: e.description.trim(), memberIds: e.memberIds, channelIds: e.channelIds };
    try {
      const g = e.id === undefined ? await this.ws.deps.api.createUserGroup(this.ws.wsId, body) : await this.ws.deps.api.updateUserGroup(this.ws.wsId, e.id, body);
      this.remember(g);
      this.closeEditor();
    } catch (err) {
      const now = this.state.editor;
      const taken = err instanceof ApiFailure && err.code === "handle_taken";
      if (now !== undefined) this.update({ editor: { ...now, saving: false, problem: taken ? groupFormProblemMessage("taken") : msg("userGroups.failed") } });
    }
  }

  /** Disable (Slack: the group can't be mentioned; members kept) or re-enable. */
  async setDisabled(id: string, disabled: boolean): Promise<void> {
    if (!this.state.canManage) return;
    try {
      this.remember(await this.ws.deps.api.updateUserGroup(this.ws.wsId, id, { disabled }));
    } catch {
      this.update({ error: msg("userGroups.failed") });
    }
  }

  async setPolicy(policy: UserGroupPolicy): Promise<void> {
    if (!this.state.canSetPolicy || policy === this.state.policy) return;
    try {
      await this.ws.deps.api.setUserGroupPolicy(this.ws.wsId, policy);
    } catch {
      this.update({ error: msg("userGroups.failed") });
    }
  }

  openCard(id: string): void {
    this.update({ card: this.cardFor(id) });
  }

  closeCard(): void {
    if (this.state.card === undefined) return;
    const { card: _c, ...rest } = this.state;
    this.setState(rest);
  }

  /** "Join N default channels" on the card: I join the group's public default channels I'm not in. */
  async joinChannels(id: string): Promise<void> {
    try {
      await this.ws.deps.api.joinUserGroupChannels(this.ws.wsId, id);
    } catch {
      this.update({ error: msg("userGroups.failed") });
    }
  }

  dismissError(): void {
    if (this.state.error === undefined) return;
    const { error: _e, ...rest } = this.state;
    this.setState(rest);
  }

  private remember(g: UserGroup): void {
    this.local[g.id] = g;
    this.derive();
  }

  private edit(fn: (e: UserGroupEditor) => UserGroupEditor): void {
    const e = this.state.editor;
    if (e === undefined || e.saving) return;
    const { problem: _p, ...rest } = e;
    this.update({ editor: fn(rest) });
  }

  private cardFor(id: string): GroupCard | undefined {
    const cache = this.ws.state.cache;
    const g = this.group(id);
    if (cache === undefined || g === undefined) return undefined;
    const ctx = { members: cache.members, channels: cache.channels, memberships: cache.memberships, meId: cache.meId, now: this.ws.deps.clock.now() };
    return groupCard(g, cache.userGroupPolicy === undefined ? ctx : { ...ctx, policy: cache.userGroupPolicy });
  }

  private derive(): void {
    const cache = this.ws.state.cache;
    if (cache === undefined) return;
    const groups = this.groups();
    const me = cache.members[cache.meId];
    const now = this.ws.deps.clock.now();
    const policy = cache.userGroupPolicy ?? DEFAULT_GROUP_POLICY;
    const patch: Partial<UserGroupsState> = {
      rows: groupRows(groups, cache.members, cache.meId, this.state.query, this.state.filter),
      counts: { active: groups.filter((g) => g.disabledAt === undefined).length, disabled: groups.filter((g) => g.disabledAt !== undefined).length },
      canManage: me !== undefined && canManageUserGroups(me, policy, now),
      policy,
      canSetPolicy: me !== undefined && canSetGroupPolicy(me, now),
    };
    const open = this.state.card;
    if (open !== undefined) {
      const card = this.cardFor(open.id);
      if (card !== undefined) patch.card = card;
    }
    this.update(patch);
  }
}
